import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import Input from '../common/Input';
import { authService } from '../../services/api';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  padding: 18px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #6b7280;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  margin-top: 6px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
`;

const SuccessMessage = styled.div`
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
`;

const RequirementsHint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: -4px;
  line-height: 1.5;
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const ShowToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  padding: 0;
  line-height: 1;

  &:hover { color: #374151; }
`;

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Compila tutti i campi');
      return;
    }
    if (newPassword.length < 6) {
      setError('La nuova password deve essere di almeno 6 caratteri');
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('La nuova password deve contenere almeno una lettera maiuscola, una minuscola e un numero');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Le nuove password non coincidono');
      return;
    }

    try {
      setLoading(true);
      const res = await authService.changePassword({ currentPassword, newPassword });
      if (res.success) {
        setSuccess('Password cambiata con successo');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(res.message || 'Errore nel cambio password');
      }
    } catch (err: any) {
      const serverErrors = err?.response?.data?.errors;
      if (serverErrors?.length) {
        setError(serverErrors.map((e: any) => e.msg).join(' — '));
      } else {
        setError(err?.response?.data?.message || 'Errore nel cambio password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Cambia Password</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        <Form onSubmit={handleSubmit}>
          <PasswordWrapper>
            <Input
              label="Password attuale"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              required
            />
            <ShowToggle type="button" onClick={() => setShowCurrent(v => !v)}>
              {showCurrent ? '🙈' : '👁️'}
            </ShowToggle>
          </PasswordWrapper>
          <PasswordWrapper>
            <Input
              label="Nuova password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
            />
            <ShowToggle type="button" onClick={() => setShowNew(v => !v)}>
              {showNew ? '🙈' : '👁️'}
            </ShowToggle>
          </PasswordWrapper>
          <RequirementsHint>
            Requisiti: almeno 6 caratteri, una lettera maiuscola, una minuscola e un numero.
          </RequirementsHint>
          <PasswordWrapper>
            <Input
              label="Conferma nuova password"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              required
            />
            <ShowToggle type="button" onClick={() => setShowConfirm(v => !v)}>
              {showConfirm ? '🙈' : '👁️'}
            </ShowToggle>
          </PasswordWrapper>
          <Actions>
            <Button variant="secondary" onClick={onClose} size="small">Chiudi</Button>
            <Button type="submit" loading={loading} size="small">Salva</Button>
          </Actions>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ChangePasswordModal;